import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { 
  LayoutDashboard, 
  Package, 
  Calendar, 
  Users, 
  FileText, 
  LogOut,
  ChevronRight,
  ChevronDown,
  MapPin,
  User,
  ShoppingBag,
  Image,
  Settings,
  Briefcase,
  Receipt,
  UserCheck,
  DollarSign,
  CalendarDays,
  UsersRound,
  Truck,
  BarChart3,
  Shield,
  Search,
  Building2,
  BedDouble,
  CreditCard,
  Upload,
  Plus,
  Star,
  FileSpreadsheet,
  PartyPopper,
  Activity
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [crmDropdownOpen, setCrmDropdownOpen] = useState(false);
  const [hotelDropdownOpen, setHotelDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hotelDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCrmDropdownOpen(false);
      }
      if (hotelDropdownRef.current && !hotelDropdownRef.current.contains(event.target)) {
        setHotelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminNav = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'User Analytics', href: '/admin/analytics/user-activity', icon: BarChart3 },
    { label: 'Agent Activity', href: '/admin/user-analytics', icon: Activity },
    { label: 'Packages', href: '/admin/packages', icon: Package },
    { label: 'Locations', href: '/admin/locations', icon: MapPin },
    { label: 'Media Gallery', href: '/admin/media', icon: Image },
    { label: 'Bookings', href: '/admin/bookings', icon: Calendar },
    { label: 'Agents', href: '/admin/agents', icon: Users },
    { label: 'Staff', href: '/admin/staff', icon: Shield },
    { label: 'Reports', href: '/admin/reports', icon: FileText },
    { label: 'SEO Settings', href: '/admin/seo', icon: Search },
    { label: 'Site Settings', href: '/admin/site-settings', icon: Settings },
    { label: 'Account Settings', href: '/admin/settings', icon: User },
  ];

  const staffNav = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'User Analytics', href: '/admin/analytics/user-activity', icon: BarChart3 },
    { label: 'Bookings', href: '/admin/bookings', icon: Calendar },
    { label: 'Reports', href: '/admin/reports', icon: FileText },
  ];

  const crmNav = [
    { label: 'Dashboard', href: '/admin/crm/dashboard', icon: LayoutDashboard },
    { label: 'Customers', href: '/admin/crm/clients', icon: Users },
    { label: 'Transport Voucher', href: '/admin/crm/vouchers', icon: Truck },
    { label: 'Sales Record', href: '/admin/crm/sales', icon: DollarSign },
  ];

  const hotelNav = [
    { label: 'Hotel Master', href: '/admin/hotels/master', icon: Building2, highlight: true },
    { label: 'Cities', href: '/admin/hotels/cities', icon: MapPin },
    { label: 'Hotels', href: '/admin/hotels/list', icon: Building2 },
    { label: 'Add Complete Hotel', href: '/admin/hotels/add-complete', icon: Plus },
    { label: 'Room Categories', href: '/admin/hotels/rooms', icon: BedDouble },
    { label: 'Pricing', href: '/admin/hotels/pricing', icon: CreditCard },
    { label: 'Rate Calendar', href: '/admin/hotels/rate-calendar', icon: CalendarDays },
    { label: 'Bulk Rate Upload', href: '/admin/hotels/bulk-rate-upload', icon: FileSpreadsheet },
    { label: 'Bulk Pricing', href: '/admin/hotels/bulk-pricing', icon: DollarSign },
    { label: 'Special Charges', href: '/admin/hotels/special-charges', icon: PartyPopper },
    { label: 'Priority (Top 15)', href: '/admin/hotels/priority', icon: Star },
    { label: 'Media Gallery', href: '/admin/hotels/media', icon: Image },
    { label: 'Homepage Settings', href: '/admin/hotels/homepage-settings', icon: Settings },
    { label: 'Import/Export', href: '/admin/hotels/bulk-upload', icon: Upload },
    { label: 'Booking Requests', href: '/admin/hotels/bookings', icon: Calendar },
    { label: 'Reports', href: '/admin/hotels/reports', icon: FileText },
  ];

  const agentNav = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'Packages', href: '/packages', icon: Package },
    { label: 'Hotels', href: '/agent/hotels', icon: Building2 },
    { label: 'Bookings', href: '/admin/bookings', icon: Calendar },
  ];

  const isStaff = () => user?.role === 'staff';
  const navItems = isAdmin() ? adminNav : isStaff() ? staffNav : agentNav;

  const isActive = (href) => location.pathname === href;
  const isCrmActive = location.pathname.startsWith('/admin/crm');
  const isHotelActive = location.pathname.startsWith('/admin/hotels') || location.pathname.startsWith('/hotels');

  return (
    <aside className="w-64 sidebar-dark text-slate-300 h-screen fixed left-0 top-0 flex flex-col overflow-hidden">
      {/* Logo */}
      <div className="p-6 border-b border-slate-700/50 flex-shrink-0">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl">
            S
          </div>
          <div className="leading-none">
            <span className="block text-lg font-bold text-white">Suvidha</span>
            <span className="block text-xs font-semibold text-primary tracking-wider">TRAVEL</span>
          </div>
        </Link>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-slate-700/50 flex-shrink-0">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent hover:scrollbar-thumb-slate-500">
        {/* CRM Top Navigation Button - Only for Admin */}
        {isAdmin() && (
          <div className="px-4 pt-4" ref={dropdownRef}>
            <div className="relative">
              <button
                onClick={() => setCrmDropdownOpen(!crmDropdownOpen)}
                data-testid="crm-nav-dropdown"
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full",
                  isCrmActive
                    ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/30"
                    : "bg-gradient-to-r from-amber-500/10 to-amber-600/10 text-amber-400 hover:from-amber-500/20 hover:to-amber-600/20 border border-amber-500/30"
                )}
              >
                <Briefcase size={20} />
                <span className="flex-1 text-left">CRM System</span>
                <ChevronDown size={16} className={cn("transition-transform", crmDropdownOpen && "rotate-180")} />
              </button>

              {/* CRM Dropdown Menu */}
              {crmDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 rounded-xl shadow-xl border border-slate-700 overflow-hidden z-50">
                  {crmNav.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setCrmDropdownOpen(false)}
                        data-testid={`crm-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all",
                          isActive(item.href)
                            ? "bg-amber-500/20 text-amber-400"
                            : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
                        )}
                      >
                        <Icon size={18} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  
                  {/* More CRM Options */}
                  <div className="border-t border-slate-700 px-4 py-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">More Options</p>
                  </div>
                  <Link
                    to="/admin/crm/agents"
                    onClick={() => setCrmDropdownOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-all",
                      isActive('/admin/crm/agents')
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                    )}
                  >
                    <UserCheck size={16} />
                    <span>CRM Agents</span>
                  </Link>
                  <Link
                    to="/admin/crm/invoices"
                    onClick={() => setCrmDropdownOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-all",
                      isActive('/admin/crm/invoices')
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                    )}
                  >
                    <Receipt size={16} />
                    <span>Invoices</span>
                  </Link>
                  <Link
                    to="/admin/crm/calendar"
                    onClick={() => setCrmDropdownOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-all",
                      isActive('/admin/crm/calendar')
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                    )}
                  >
                    <CalendarDays size={16} />
                    <span>Calendar</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Hotel Booking Navigation Button - Only for Admin */}
        {isAdmin() && (
          <div className="px-4 pt-2" ref={hotelDropdownRef}>
            <div className="relative">
              <button
                onClick={() => setHotelDropdownOpen(!hotelDropdownOpen)}
                data-testid="hotel-nav-dropdown"
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full",
                  isHotelActive
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                    : "bg-gradient-to-r from-blue-500/10 to-blue-600/10 text-blue-400 hover:from-blue-500/20 hover:to-blue-600/20 border border-blue-500/30"
                )}
              >
                <Building2 size={20} />
                <span className="flex-1 text-left">Hotel Booking</span>
                <ChevronDown size={16} className={cn("transition-transform", hotelDropdownOpen && "rotate-180")} />
              </button>

              {/* Hotel Dropdown Menu */}
              {hotelDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 rounded-xl shadow-xl border border-slate-700 overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
                  {hotelNav.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setHotelDropdownOpen(false)}
                        data-testid={`hotel-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all",
                          isActive(item.href)
                            ? "bg-blue-500/20 text-blue-400"
                            : item.highlight
                              ? "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border-l-2 border-amber-500"
                              : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
                        )}
                      >
                        <Icon size={18} />
                        <span>{item.label}</span>
                        {item.highlight && (
                          <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-amber-500 text-slate-900 rounded font-bold">NEW</span>
                        )}
                      </Link>
                    );
                  })}
                  
                  {/* Customer Search Link */}
                  <div className="border-t border-slate-700 px-4 py-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Customer View</p>
                  </div>
                  <Link
                    to="/hotels"
                    onClick={() => setHotelDropdownOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2 text-sm font-medium transition-all",
                      isActive('/hotels')
                        ? "bg-blue-500/20 text-blue-400"
                        : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                    )}
                  >
                    <Search size={16} />
                    <span>Search Hotels</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          <p className="text-xs text-slate-500 uppercase tracking-wider px-4 py-2">Main Menu</p>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all group",
                  isActive(item.href)
                    ? "bg-primary text-white shadow-lg shadow-primary/25"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                )}
              >
                <Icon size={20} />
                <span className="flex-1">{item.label}</span>
                {isActive(item.href) && <ChevronRight size={16} />}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Actions - Fixed */}
      <div className="p-4 border-t border-slate-700/50 flex-shrink-0">
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout"
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-rose-400 hover:bg-rose-500/10 w-full transition-colors"
        >
          <LogOut size={20} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
